import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { track } from '@/lib/analytics';
import { X, Guitar, Piano, Mic, Drum, Music, Sparkles, ArrowRight, PlayCircle } from 'lucide-react';

const KEY = 'maestro_onboarded_v1';

const INSTRUMENTS = [
  { key: 'guitar', label: 'Guitare', Icon: Guitar },
  { key: 'piano', label: 'Piano', Icon: Piano },
  { key: 'voice', label: 'Chant', Icon: Mic },
  { key: 'drums', label: 'Batterie', Icon: Drum },
  { key: 'bass', label: 'Basse', Icon: Music },
];

export default function OnboardingModal() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [instrument, setInstrument] = useState(null);
  const [songs, setSongs] = useState([]);

  useEffect(() => {
    try {
      if (localStorage.getItem(KEY)) return;
      setOpen(true);
      track('onboarding_start');
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (step === 1 && instrument) {
      (async () => {
        const { data } = await api.get('/songs', { params: { instrument, difficulty: 'beginner' } });
        setSongs(data.slice(0, 4));
      })();
    }
  }, [step, instrument]);

  const finish = () => {
    try { localStorage.setItem(KEY, '1'); } catch {}
    setOpen(false);
    track('onboarding_finish', { instrument });
  };

  const skip = () => {
    finish();
    track('onboarding_skip', { step });
  };

  const openSong = async (song) => {
    try {
      await api.post('/progress', { song_id: song.id, status: 'learning', minutes_practiced: 0 });
    } catch { /* noop */ }
    finish();
    track('onboarding_song_selected', { song_id: song.id, title: song.title });
    navigate(`/song/${song.id}`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="glass rounded-3xl max-w-2xl w-full p-6 md:p-10 relative">
        <button data-testid="onboarding-close" onClick={skip} className="absolute top-4 right-4 text-stone-400 hover:text-stone-200 p-2">
          <X size={18} />
        </button>

        {/* progress dots */}
        <div className="flex gap-2 mb-8 justify-center">
          {[0, 1, 2].map(i => (
            <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-8 bg-orange-500' : i < step ? 'w-6 bg-orange-800' : 'w-6 bg-stone-700'}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="text-center">
            <Sparkles className="mx-auto text-orange-400 mb-3" size={36} />
            <div className="overline text-orange-400 mb-2">Bienvenue chez Maestro</div>
            <h2 className="font-display text-4xl leading-tight mb-4">Ton premier morceau<br/><em className="not-italic text-orange-500">en 3 étapes.</em></h2>
            <p className="text-stone-400 max-w-md mx-auto">
              On va choisir ton instrument, une chanson simple, et te lancer dans le Play-Along. Ça prend 30 secondes.
            </p>
            <button data-testid="onboarding-next-0" onClick={() => { setStep(1); track('onboarding_step', { step: 1 }); }} className="btn-primary mt-8 inline-flex items-center gap-2">
              C'est parti <ArrowRight size={16} />
            </button>
          </div>
        )}

        {step === 1 && (
          <div>
            <div className="overline text-orange-400 mb-2 text-center">Étape 1 / 2</div>
            <h2 className="font-display text-3xl mb-6 text-center">Quel instrument tu joues ?</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {INSTRUMENTS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  data-testid={`onboarding-inst-${key}`}
                  onClick={() => { setInstrument(key); setStep(2); track('onboarding_instrument', { instrument: key }); }}
                  className={`p-4 rounded-2xl border text-center transition-all ${
                    instrument === key ? 'border-orange-600 bg-orange-950/40' : 'border-stone-800 hover:border-stone-600'
                  }`}
                >
                  <Icon size={26} className="mx-auto text-orange-400 mb-2" />
                  <div className="font-display text-lg">{label}</div>
                </button>
              ))}
            </div>
            <div className="text-center mt-6">
              <button data-testid="onboarding-back-1" onClick={() => setStep(0)} className="text-stone-400 text-sm hover:text-stone-200">← Retour</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="overline text-orange-400 mb-2 text-center">Étape 2 / 2</div>
            <h2 className="font-display text-3xl mb-2 text-center">Choisis une chanson</h2>
            <p className="text-stone-400 text-sm text-center mb-6">On l'ajoute à ta pratique et on ouvre le Play-Along.</p>
            {songs.length === 0 ? (
              <div className="text-stone-500 py-6 text-center">Aucune chanson trouvée pour cet instrument.</div>
            ) : (
              <div className="space-y-2">
                {songs.map((s, i) => (
                  <button
                    key={s.id}
                    data-testid={`onboarding-song-${i}`}
                    onClick={() => openSong(s)}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-stone-800 hover:border-orange-800/60 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-stone-800 overflow-hidden shrink-0">
                      {s.cover_url && <img src={s.cover_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg truncate">{s.title}</div>
                      <div className="text-stone-400 text-sm truncate">{s.artist}</div>
                    </div>
                    <PlayCircle size={20} className="text-orange-400" />
                  </button>
                ))}
              </div>
            )}
            <div className="text-center mt-6">
              <button data-testid="onboarding-back-2" onClick={() => setStep(1)} className="text-stone-400 text-sm hover:text-stone-200">← Retour</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
